import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './level_3.css';
import Button from '../components/button';
import Instructions from '../components/instructions';
import Settings from '../components/settings';
import SoundVideo from '../components/sound_video';
import level3IntroVideo from '../assets/level3introvid.mp4';
import level2Instructions from '../assets/level2instructions.png';
import level3Background from '../assets/level3bg.png';
import pause from '../assets/pause.png';
import arrow from '../assets/arrow.png';
import girlBack from '../assets/girlback.png';
import girlFront from '../assets/girlfront.png';
import girlSide from '../assets/girlside.png';
import ghost from '../assets/ghost.png';
import treasure from '../assets/tressure.png';
import retry from '../assets/retry.png';
import homeButton from '../assets/homebutton.png';
import nextButton from '../assets/button2.png';
import yellowStar from '../assets/yellowstar.png';
import purpleStar from '../assets/purplestar.png';
import footstepSound from '../assets/footstep.m4a';
import winSound from '../assets/winsound.m4a';
import loseSound from '../assets/losesound.mp3';
import { play_sound, stop_sound } from '../audio_settings';
import { getLevelTime } from './level_time';

const MAP_WIDTH = 941;
const MAP_HEIGHT = 1672;
const MAP_SCALE = 1.42;
const VIEWPORT_WIDTH = 428;
const VIEWPORT_HEIGHT = 926;
const MOVE_TIME = 560;
const FALL_DISTANCE = 115;
const LEVEL_TIME = getLevelTime(3);

const PATH_NODES = {
  start: { x: 445, y: 1600, up: 'lower_bridge' },
  lower_bridge: { x: 445, y: 1350, up: 'lower_junction', down: 'start' },
  lower_junction: {
    x: 445,
    y: 1125,
    left: 'lower_curve',
    right: 'winged_platform',
    down: 'lower_bridge'
  },
  winged_platform: {
    x: 550,
    y: 940,
    up: 'right_upper_junction',
    right: 'spear_path',
    left: 'lower_junction'
  },
  spear_path: {
    x: 730,
    y: 1050,
    up: 'spear_upper_split',
    left: 'winged_platform',
    right: 'spear_trap'
  },
  spear_trap: { x: 800, y: 1140, left: 'spear_path', deadEnd: true },
  spear_upper_split: {
    x: 755,
    y: 980,
    left: 'long_path_entry',
    right: 'cobra_trap',
    down: 'spear_path'
  },
  right_upper_junction: {
    x: 655,
    y: 970,
    left: 'long_path_entry',
    right: 'cobra_trap',
    down: 'winged_platform'
  },
  cobra_trap: { x: 925, y: 860, left: 'right_upper_junction', deadEnd: true },
  long_path_entry: { x: 690, y: 875, up: 'long_path_turn', right: 'right_upper_junction' },
  long_path_turn: { x: 690, y: 760, left: 'long_path_junction', down: 'long_path_entry' },
  long_path_junction: { x: 530, y: 660, left: 'upper_bridge', right: 'long_path_turn' },
  lower_curve: { x: 340, y: 1030, up: 'left_turn', right: 'lower_junction' },
  left_turn: { x: 255, y: 900, right: 'ankh_platform', left: 'chest_trap', down: 'lower_curve' },
  chest_trap: { x: 100, y: 815, right: 'left_turn', deadEnd: true },
  ankh_platform: {
    x: 470,
    y: 760,
    up: 'upper_bridge',
    left: 'left_turn'
  },
  upper_bridge: {
    x: 470,
    y: 650,
    up: 'upper_junction',
    down: 'ankh_platform',
    right: 'center_right_split'
  },
  disk_platform: {
    x: 700,
    y: 535,
    up: 'spike_path',
    down: 'right_upper_junction',
    left: 'right_upper_junction'
  },
  spike_path: { x: 730, y: 410, up: 'spike_trap', down: 'disk_platform' },
  spike_trap: { x: 760, y: 315, down: 'spike_path', deadEnd: true },
  upper_junction: {
    x: 470,
    y: 625,
    up: 'door_path',
    down: 'upper_bridge',
    left: 'claw_path'
  },
  center_right_split: {
    x: 555,
    y: 640,
    up: 'disk_platform',
    down: 'right_upper_junction',
    left: 'upper_bridge'
  },
  claw_path: { x: 340, y: 535, right: 'upper_junction', left: 'claw_trap' },
  claw_trap: { x: 250, y: 365, right: 'claw_path', deadEnd: true },
  door_path: { x: 470, y: 420, up: 'door', down: 'upper_junction' },
  door: { x: 470, y: 255, down: 'door_path', door: true }
};

const CHARACTER_IMAGES = {
  up: girlBack,
  down: girlFront,
  left: girlSide,
  right: girlSide
};

const FALL_OFFSETS = {
  up: { x: 0, y: -FALL_DISTANCE },
  down: { x: 0, y: FALL_DISTANCE },
  left: { x: -FALL_DISTANCE, y: 0 },
  right: { x: FALL_DISTANCE, y: 0 }
};

const Level3 = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showInstructions, setShowInstructions] = useState(true);
  const [showPause, setShowPause] = useState(false);
  const [nodeId, setNodeId] = useState('start');
  const [direction, setDirection] = useState('up');
  const [isMoving, setIsMoving] = useState(false);
  const [isFalling, setIsFalling] = useState(false);
  const [fallOffset, setFallOffset] = useState({ x: 0, y: 0 });
  const [timeLeft, setTimeLeft] = useState(LEVEL_TIME);
  const movementTimerRef = useRef(null);
  const footstepAudioRef = useRef(null);
  const winAudioRef = useRef(null);
  const loseAudioRef = useRef(null);
  const introActive = location.pathname === '/level_3/intro';
  const won = location.pathname === '/level_3/win';
  const lost = location.pathname === '/level_3/lose';
  const resultActive = won || lost;
  const currentNode = PATH_NODES[nodeId];
  const gameplayBlocked = showInstructions || showPause || props.showSettings || resultActive || isMoving || isFalling;

  useEffect(() => {
    const winAudio = winAudioRef.current;
    const loseAudio = loseAudioRef.current;
    const resultAudio = won ? winAudio : lost ? loseAudio : null;

    if (!resultAudio) return;

    play_sound(resultAudio);
    return () => stop_sound(resultAudio);
  }, [lost, won]);

  useEffect(() => {
    const resultAudio = won
      ? winAudioRef.current
      : lost
        ? loseAudioRef.current
        : null;

    if (!resultAudio) return;

    if (showPause || props.showSettings) {
      resultAudio.pause();
    } else {
      play_sound(resultAudio, { restart: false });
    }
  }, [lost, props.showSettings, showPause, won]);

  useEffect(() => {
    if (introActive || showInstructions || showPause || props.showSettings || resultActive) return;

    const timer = window.setInterval(() => {
      setTimeLeft((time) => {
        if (time === 1) {
          navigate('/level_3/lose');
        }

        return Math.max(0, time - 1);
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [introActive, navigate, props.showSettings, resultActive, showInstructions, showPause]);

  useEffect(() => {
    const footstepAudio = footstepAudioRef.current;
    const winAudio = winAudioRef.current;
    const loseAudio = loseAudioRef.current;

    const stopDisabledSounds = (event) => {
      if (event.detail.name === 'sound' && !event.detail.value) {
        stop_sound(footstepAudio);
        stop_sound(winAudio);
        stop_sound(loseAudio);
      }
    };

    window.addEventListener('audio_setting_change', stopDisabledSounds);

    return () => {
      window.removeEventListener('audio_setting_change', stopDisabledSounds);
      window.clearTimeout(movementTimerRef.current);
      stop_sound(footstepAudio);
      stop_sound(winAudio);
      stop_sound(loseAudio);
    };
  }, []);

  const move = useCallback((moveDirection) => {
    if (gameplayBlocked) return;

    const nextNodeId = currentNode[moveDirection];
    const nextNode = PATH_NODES[nextNodeId];

    setDirection(moveDirection);
    setIsMoving(true);
    play_sound(footstepAudioRef.current, { loop: true });

    if (!nextNode) {
      setFallOffset(FALL_OFFSETS[moveDirection]);
      setIsFalling(true);

      movementTimerRef.current = window.setTimeout(() => {
        stop_sound(footstepAudioRef.current);
        navigate('/level_3/lose');
      }, MOVE_TIME);
      return;
    }

    setNodeId(nextNodeId);

    movementTimerRef.current = window.setTimeout(() => {
      stop_sound(footstepAudioRef.current);
      setIsMoving(false);

      if (nextNode.deadEnd) {
        navigate('/level_3/lose');
      } else if (nextNode.door) {
        navigate('/level_3/win');
      }
    }, MOVE_TIME);
  }, [currentNode, gameplayBlocked, navigate]);

  useEffect(() => {
    const keyDirections = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right'
    };
    const handleKeyDown = (event) => {
      if (keyDirections[event.key]) move(keyDirections[event.key]);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [move]);

  const retryLevel = () => {
    setNodeId('start');
    setDirection('up');
    setIsMoving(false);
    setIsFalling(false);
    setFallOffset({ x: 0, y: 0 });
    setTimeLeft(LEVEL_TIME);
    setShowInstructions(true);
    navigate('/level_3/trail');
  };

  const finishLevel = () => {
    props.unlockLevel4();
    navigate('/levels');
  };

  const playerPosition = {
    x: currentNode.x + fallOffset.x,
    y: currentNode.y + fallOffset.y
  };
  const scaledMapWidth = MAP_WIDTH * MAP_SCALE;
  const scaledMapHeight = MAP_HEIGHT * MAP_SCALE;
  const cameraX = Math.min(0, Math.max(
    VIEWPORT_WIDTH - scaledMapWidth,
    (VIEWPORT_WIDTH / 2) - (playerPosition.x * MAP_SCALE)
  ));
  const cameraY = Math.min(0, Math.max(
    VIEWPORT_HEIGHT - scaledMapHeight,
    (VIEWPORT_HEIGHT * 0.62) - (playerPosition.y * MAP_SCALE)
  ));
  const playerClassName = `level_3_player level_3_player_${direction}${isMoving ? ' level_3_player_moving' : ''}${isFalling ? ' level_3_player_falling' : ''}`;
  const timerText = `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`;
  const timerIsUrgent = timeLeft > 0 && timeLeft <= 10;

  if (introActive) {
    return (
      <div className="level_3_intro">
        <SoundVideo
          className="level_3_intro_video"
          src={level3IntroVideo}
          autoPlay
          playsInline
          onEnded={() => navigate('/level_3/trail')}
        />
        <Button className="level_3_intro_skip" onclick={() => navigate('/level_3/trail')}>
          <img src={nextButton} alt="Skip intro" />
        </Button>
      </div>
    );
  }

  return (
    <div className="level_3">
      <audio ref={footstepAudioRef} src={footstepSound} />
      <audio ref={winAudioRef} src={winSound} />
      <audio ref={loseAudioRef} src={loseSound} />

      <div className="level_3_map_viewport">
        <div
          className="level_3_map"
          style={{
            width: scaledMapWidth,
            height: scaledMapHeight,
            backgroundImage: `url(${level3Background})`,
            backgroundSize: `${scaledMapWidth}px ${scaledMapHeight}px`,
            transform: `translate(${cameraX}px, ${cameraY}px)`
          }}
        >
          <img
            className={playerClassName}
            src={CHARACTER_IMAGES[direction]}
            alt="Girl exploring the temple paths"
            style={{
              left: playerPosition.x * MAP_SCALE,
              top: playerPosition.y * MAP_SCALE
            }}
          />
        </div>
      </div>

      <p className="level_3_status">
        Choose a direction. A direction without a stone trail will make you fall.
      </p>

      <div className="level_3_controls">
        {['up', 'left', 'down', 'right'].map((moveDirection) => (
          <button
            className={`level_3_control level_3_control_${moveDirection}`}
            key={moveDirection}
            type="button"
            data-no-feedback
            disabled={gameplayBlocked}
            onClick={() => move(moveDirection)}
          >
            <img src={arrow} alt={moveDirection} />
          </button>
        ))}
      </div>

      <div className="level_3_top">
        <Button className="level_3_pause" onclick={() => {
          if (!isMoving && !isFalling) setShowPause(true);
        }}>
          <img src={pause} alt="Pause" />
        </Button>
        <div className={`level_3_timer${timerIsUrgent ? ' timer_urgent' : ''}`}>
          <span>{timerText}</span>
        </div>
      </div>

      {props.showSettings && (
        <Settings onclose={() => props.setShowSettings(false)} />
      )}

      {showInstructions && (
        <Instructions
          className="level_3_instructions"
          backgroundImage={level2Instructions}
          title={<>Level 3<br />Instructions</>}
          text={[
            'The evil ghost took the cat into a maze of dangerous temple trails.',
            'Use the four arrows to move from one trail point to the next. Every direction stays available.',
            'Choose carefully. A direction without a stone trail makes you fall, and side trails end in traps.'
          ]}
          onStart={() => setShowInstructions(false)}
        />
      )}

      {showPause && (
        <div className="pause_overlay">
          <div className="pause_panel">
            <h1>Paused</h1>
            <Button className="pause_menu_button" onclick={() => props.setShowSettings(true)}>Settings</Button>
            <Button className="pause_menu_button" onclick={() => navigate('/home')}>Home</Button>
            <Button className="pause_menu_button" onclick={() => navigate('/levels')}>Levels</Button>
            <Button className="pause_menu_button" onclick={() => setShowPause(false)}>Resume</Button>
          </div>
        </div>
      )}

      {resultActive && (
        <div className="level_3_result_overlay">
          <div className="level_3_result_panel">
            {lost ? (
              <>
                <h1>You Lose!</h1>
                <img className="level_3_result_ghost" src={ghost} alt="" />
                <p className="level_3_result_message level_3_result_message_lose">
                  You reached a trap or stepped where there was no trail.
                </p>
                <div className="level_3_result_actions">
                  <Button className="level_3_result_button" onclick={retryLevel}>
                    <img src={retry} alt="Retry" />
                  </Button>
                  <Button className="level_3_result_button" onclick={() => navigate('/home')}>
                    <img src={homeButton} alt="Home" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h1>Level<br />Completed!</h1>
                <div className="level_3_result_stars">
                  <img src={yellowStar} alt="" />
                  <img src={yellowStar} alt="" />
                  <img src={purpleStar} alt="" />
                </div>
                <img className="level_3_result_treasure" src={treasure} alt="" />
                <div className="level_3_result_actions">
                  <Button className="level_3_result_button" onclick={retryLevel}>
                    <img src={retry} alt="Retry" />
                  </Button>
                  <Button className="level_3_result_button" onclick={finishLevel}>
                    <img src={nextButton} alt="Next level" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Level3;
